import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, ArrowRight } from "lucide-react";
import Layout, { Breadcrumbs } from "../components/Layout";
import Seo from "../components/Seo";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { getTodayResult } from "../lib/api";
import { todayDDMMYYYY } from "../lib/date";

const ACCENT = "#ef5f3c";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog/" },
];

const overview = [
  ["Game Type", "Traditional archery based number game"],
  ["Popular Region", "Bhutan & North-East India"],
  ["Result Method", "Based on arrow count"],
  ["Daily Results", "Released according to the official schedule"],
  ["Number Range", "Two-digit winning number"],
  ["Interest Level", "Very high among regular followers"],
  ["Result Tracking", "Online and offline"],
  ["Analysis", "Weekly and monthly reviews"],
];

const comparison = [
  ["Based on Archery", "Yes", "No"],
  ["Live Event", "Yes", "Usually No"],
  ["Cultural Heritage", "Strong", "Limited"],
  ["Daily Results", "Yes", "Depends on the game"],
  ["Community Discussions", "Very Active", "Varies"],
];

const analyseWays = [
  "First, find the previous results of Bhutan Morning Teer",
  "Note the repeated counter numbers of Bhutan Morning Teer",
  "Find the common numbers of Bhutan Morning Teer",
  "Match the Bhutan Morning Teer dream numbers",
  "Match any missing numbers with the previous results",
  "Follow the weekly trend",
];

const tips = [
  "Learn how the results are calculated from the archery rounds",
  "Follow a trusted results platform — our site is updated as soon as counts finish",
  "Track previous results together with common numbers and dream numbers",
  "Stay curious — the experience grows the more you observe",
];

const faqs = [
  [
    "What is the result of Bhutan Morning Teer?",
    "The Bhutan Morning Teer result is a two-digit number that comes from a real archery round. Officials count all the arrows shot at the target and the final total becomes the day's official F/R and S/R result.",
  ],
  [
    "What is Bhutan Morning Teer?",
    "Bhutan Morning Teer is a traditional archery-based number game. Two results are declared every morning — F/R around 10:30 AM and S/R around 11:30 AM.",
  ],
  [
    "How is Bhutan Morning Teer different from other games?",
    "Unlike computer or software based games, Bhutan Morning Teer numbers are decided by real archers shooting arrows, which keeps a living cultural tradition alive.",
  ],
  [
    "Can the record of previous Bhutan Morning Teer results affect future results?",
    "No number game can guarantee future outcomes. However, many followers study previous results, common numbers and dream numbers as a long-standing tradition.",
  ],
  [
    "Why do people follow the Bhutan Morning Teer results every day?",
    "For many people checking the morning numbers has become a small daily habit and a part of the local routine and culture.",
  ],
  [
    "Is Bhutan Morning Teer popular only in one region?",
    "No. Interest in Bhutan Morning Teer, Bhutan Teer and Bhutan Night Teer now reaches far beyond one region, as the internet makes the numbers easy to check anywhere.",
  ],
  [
    "What is the best way to check the official Bhutan Morning Teer results?",
    "Follow a trusted results platform like this site, which is updated as soon as the arrow counts are finished each morning.",
  ],
  [
    "Why do players keep records of previous Bhutan Morning Teer results?",
    "Keeping records helps followers recognise how numbers behave over time and makes the weekly and monthly analysis more interesting.",
  ],
];

function SectionTitle({ children }) {
  return (
    <h2 className="mt-8 border-b border-slate-200 pb-2 text-xl font-bold text-[#10161f] sm:text-2xl">
      {children}
    </h2>
  );
}

function Para({ children }) {
  return <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{children}</p>;
}

function ResultCard({ date, result }) {
  const fr = result?.fr || "--";
  const sr = result?.sr || "--";
  return (
    <div
      data-testid="blog-result-card"
      className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-[#10161f] sm:text-lg">
          Bhutan Morning Teer Result
        </h2>
        <span
          className="flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: ACCENT }}
        >
          <Calendar size={15} /> {date}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {[
          { label: "F/R (10:30 AM)", val: fr },
          { label: "S/R (11:30 AM)", val: sr },
        ].map((c) => (
          <div
            key={c.label}
            className="flex flex-col items-center rounded-xl bg-slate-50 py-4"
          >
            <span className="text-xs text-slate-500 sm:text-sm">{c.label}</span>
            <span
              className="mt-2 flex h-16 w-16 items-center justify-center rounded-full border-[3px] text-2xl font-extrabold text-[#10161f]"
              style={{ borderColor: ACCENT }}
            >
              {c.val}
            </span>
          </div>
        ))}
      </div>

      <Link
        to="/previous-result/"
        data-testid="blog-check-previous"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold"
        style={{ color: ACCENT }}
      >
        Check Previous Results <ArrowRight size={15} />
      </Link>
    </div>
  );
}

function DataTable({ head, rows }) {
  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr style={{ background: ACCENT }} className="text-white">
            {head.map((h) => (
              <th key={h} className="px-3 py-2.5 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="odd:bg-white even:bg-slate-50">
              {r.map((cell, j) => (
                <td
                  key={j}
                  className={`border-t border-slate-100 px-3 py-2.5 ${
                    j === 0 ? "font-semibold text-[#10161f]" : "text-slate-700"
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Blog() {
  const date = todayDDMMYYYY("/");
  const { data } = useQuery({ queryKey: ["today-result"], queryFn: getTodayResult });
  const result = data?.result;

  return (
    <Layout>
      <Seo
        title={`Bhutan Morning Teer Result ${date} | Bhutan Teer Result & Bhutan Morning Teer`}
        description="Bhutan Morning Teer blog – get daily updates for Bhutan Morning Teer and Bhutan Teer results, common numbers, dream numbers, previous records and useful information all in one clean place."
        path="/blog/"
        breadcrumb={crumbs}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: `Bhutan Morning Teer Result ${date} | Bhutan Teer Result & Bhutan Morning Teer`,
          description:
            "Bhutan Morning Teer blog – daily Bhutan Morning Teer and Bhutan Teer results, common numbers, dream numbers, previous records and useful information.",
          image: "https://bhutanmorningteerresult.in/og-image.jpg",
          author: { "@type": "Organization", name: "Bhutan Morning Teer Result" },
          publisher: {
            "@type": "Organization",
            name: "Bhutan Morning Teer Result",
            logo: {
              "@type": "ImageObject",
              url: "https://bhutanmorningteerresult.in/logo512.png",
            },
          },
          mainEntityOfPage: "https://bhutanmorningteerresult.in/blog/",
        }}
      />
      <Breadcrumbs items={crumbs} />

      <article className="mx-auto max-w-3xl px-4 py-4">
        <h1 className="text-2xl font-extrabold leading-snug text-[#10161f] sm:text-4xl">
          Bhutan Morning Teer Result {date} | Bhutan Teer Result & Bhutan Morning
          Teer
        </h1>
        <Para>
          Welcome to Bhutan Morning Teer. Get the fastest daily updates for{" "}
          <b>Bhutan Morning Teer</b> and <b>Bhutan Teer</b> results, common
          numbers, dream numbers, previous records and useful information — all
          in one clean place.
        </Para>

        <ResultCard date={date} result={result} />

        <Para>
          If you follow the teer game, you probably also follow the{" "}
          <b>Bhutan Morning Teer Result</b> and want to check the numbers at
          least once a day. For many, visiting the site each morning has become
          part of the daily routine and culture.
        </Para>
        <Para>
          The popularity of <b>Bhutan Morning Teer</b>, <b>Bhutan Teer</b> and{" "}
          <b>Bhutan Night Teer</b> is steadily rising because the game is not
          computer-based or a mobile casino — it is played by real archers, and
          that traditional method has grown into a widely followed number game.
        </Para>

        <SectionTitle>Overview of Bhutan Morning Teer</SectionTitle>
        <DataTable head={["Topic", "Details"]} rows={overview} />
        <p className="mt-3 text-[15px] italic leading-relaxed text-slate-600">
          This brief overview shows why <b>Bhutan Morning Teer</b> is one of the
          most popular archery-based number games.
        </p>

        <SectionTitle>What is Bhutan Morning Teer?</SectionTitle>
        <Para>
          Bhutan Morning Teer is a numbers game based on archery. It is not like
          a simple lottery — the winning number comes from a real archery
          competition in which trained archers shoot hundreds of arrows at a
          target. Officials carefully count each arrow, and the final total
          becomes the official Bhutan Morning Teer result.
        </Para>
        <Para>
          This old method gives the game a special character. It is a blend of
          tradition, skill and social participation rather than a machine-driven
          activity.
        </Para>

        <SectionTitle>Why do people search for Bhutan Morning Teer?</SectionTitle>
        <Para>
          Many people search for Bhutan Morning Teer especially in the morning,
          when the day's numbers attract extra attention. They also love
          comparing past results with today's numbers and looking up common or
          dream numbers before the round.
        </Para>
        <Para>
          For some it is a long-standing hobby; for others it has become a small
          daily habit — a cup of tea and a quick check of the morning numbers.
        </Para>

        <SectionTitle>How does Bhutan Morning Teer work?</SectionTitle>
        <Para>
          Bhutan Morning Teer is popular because the process is simple.
          Experienced archers gather in a shooting round and take aim at their
          targets. When the round ends, officials count every arrow and tally
          the hits. The final two-digit number becomes the official result.
        </Para>
        <Para>
          The archers shoot in two sessions — one around 10:30 AM and the other
          around 11:30 AM — so two numbers (F/R and S/R) are announced each day.
        </Para>

        <SectionTitle>Why is Bhutan Morning Teer different from others?</SectionTitle>
        <Para>
          Many number games rely on computers or software to pick winning
          numbers. Bhutan Morning Teer is different — real archers determine the
          outcome, keeping visitors connected with a living cultural tradition.
        </Para>

        <SectionTitle>Why do the results of Bhutan Morning Teer differ every day?</SectionTitle>
        <Para>
          No archer can shoot the same arrow twice. Weather, the archer's focus
          and small differences in shooting time all influence where each arrow
          lands, so the final number naturally changes from day to day. That
          unpredictability is a big part of the game's charm.
        </Para>

        <SectionTitle>When are Bhutan Morning Teer results published?</SectionTitle>
        <Para>
          Bhutan Morning Teer publishes the results at two times — 10:30 AM and
          11:30 AM. Announcements are made after the archers finish shooting and
          officials count every arrow. Many visitors set a daily routine around
          this window so they never miss the update.
        </Para>

        <SectionTitle>Cultural significance of Bhutan Morning Teer</SectionTitle>
        <Para>
          Bhutan Morning Teer is more than numbers — it is deeply linked to
          traditional archery. Archery is respected as both a cultural activity
          and a sport, and Teer keeps the tradition alive while remaining
          approachable to modern audiences.
        </Para>

        <SectionTitle>How to read the Bhutan Morning Teer numbers</SectionTitle>
        <Para>
          Understanding the numbers takes a little study. Many regular followers
          feel that reviewing past outcomes helps them recognise how numbers
          behave over time.
        </Para>
        <Para>
          No number game can guarantee future outcomes, but analysing history
          has become a tradition in itself — which is why so many visitors look
          for <b>Bhutan Morning Teer Previous Results</b>.
        </Para>

        <SectionTitle>Popular ways to analyse Bhutan Morning Teer results</SectionTitle>
        <Para>When reviewing history, followers usually try a mix of these methods:</Para>
        <ul className="mt-2 list-disc space-y-1.5 pl-6 text-[15px] text-slate-700">
          {analyseWays.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>

        <SectionTitle>Bhutan Morning Teer weekly analysis</SectionTitle>
        <Para>
          Weekly analysis lets followers watch short-term shifts in the number
          pattern instead of focusing on a single day. Players note recurring
          numbers and interesting positions, then compare them across the next
          seven days.
        </Para>

        <SectionTitle>Comparison with other number games</SectionTitle>
        <DataTable
          head={["Feature", "Bhutan Morning Teer", "Typical Number Games"]}
          rows={comparison}
        />

        <SectionTitle>Tips for Bhutan Morning Teer players</SectionTitle>
        <ul className="mt-2 list-disc space-y-1.5 pl-6 text-[15px] text-slate-700">
          {tips.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>

        <SectionTitle>Why is Bhutan Morning Teer so popular?</SectionTitle>
        <Para>
          Interest in Bhutan Morning Teer now reaches far and wide. People from
          many regions look up the results every day, and the internet has made
          the numbers easy to check anywhere. On the other side, the traditional
          archers keep the game charming and authentic.
        </Para>

        <SectionTitle>FAQs about Bhutan Morning Teer</SectionTitle>
        <Accordion type="single" collapsible className="mt-3" data-testid="blog-faq">
          {faqs.map(([q, a], i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white"
            >
              <AccordionTrigger className="px-4 text-[15px] font-semibold text-[#10161f] hover:no-underline">
                {i + 1}. {q}
              </AccordionTrigger>
              <AccordionContent className="px-4 text-[15px] leading-relaxed text-slate-600">
                {a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <SectionTitle>Final thoughts</SectionTitle>
        <Para>
          Bhutan Morning Teer results are more than a daily number announcement —
          they carry a slice of culture. Archers decide the outcome through their
          own skill, giving the game a physical, human quality that other number
          games simply do not have.
        </Para>
        <Para>
          The steady popularity of Bhutan Morning Teer shows how much people
          value the unique mix of culture, history and daily excitement. Follow
          trusted result sources, review the history, and enjoy the tradition.
        </Para>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/previous-result/"
            data-testid="blog-btn-previous"
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-[#10161f] transition-colors hover:bg-slate-100"
          >
            Previous Results
          </Link>
          <Link
            to="/common-number/"
            data-testid="blog-btn-common"
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-[#10161f] transition-colors hover:bg-slate-100"
          >
            Common Number
          </Link>
          <Link
            to="/dream-number/"
            data-testid="blog-btn-dream"
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-[#10161f] transition-colors hover:bg-slate-100"
          >
            Dream Numbers
          </Link>
          <Link
            to="/"
            data-testid="blog-btn-home"
            className="rounded-full bg-[#0c1622] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1c2b3d]"
          >
            Back to Home
          </Link>
        </div>
      </article>
    </Layout>
  );
}
